import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import PlayerDemo from "./components/PlayerDemo";
import {
  formatPlayCount,
  getCategoryById,
  getCategoryName,
  getRelatedVideos,
  getVideoById,
} from "@/data/videos";
import useFavorites from "@/hooks/useFavorites";
import usePageMeta from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { videoId = "" } = useParams();
  const video = getVideoById(videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(
    () => (video ? getRelatedVideos(video, 5) : []),
    [video],
  );

  usePageMeta(
    video
      ? `${video.title}免费在线观看 - 视频详情 - 免费视频网站91`
      : "视频详情 - 免费视频网站91",
    video
      ? `免费视频网站91 提供《${video.title}》免费在线观看，${video.description}高清流畅观影体验。`
      : "免费视频网站91 视频详情页，查看视频简介、相关推荐与免费在线观看入口。",
    video
      ? `免费视频网站91,${video.title},${video.type},免费在线观看,免费高清视频`
      : "免费视频网站91,视频详情,免费在线观看,免费高清视频",
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!video) {
    return (
      <div className="container-page flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-file-forbid-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          视频不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          你访问的视频可能已被移除或链接有误，先去首页逛逛吧。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const category = getCategoryById(video.categoryId);
  const favorited = isFavorite(video.id);

  const handleFavorite = () => {
    toggleFavorite(video.id);
    setToast(favorited ? "已取消收藏" : "已加入收藏，可在本地随时回看");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <>
      <div className="border-b border-background-200/70 bg-background-100">
        <div className="container-page py-4">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              {
                label: getCategoryName(video.categoryId),
                to: `/category/${video.categoryId}`,
              },
              { label: video.title },
            ]}
          />
        </div>
      </div>

      <div className="container-page py-6 md:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <PlayerDemo video={video} />

            <div className="mt-5">
              <h1 className="font-heading text-xl font-extrabold leading-snug text-foreground-950 md:text-2xl">
                {video.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {video.uploadedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatPlayCount(video.playCount)} 次播放
                </span>
                <span className="flex items-center gap-1 font-label">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating} 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {category && (
                  <Link
                    to={`/category/${category.id}`}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                  >
                    <i className={category.icon}></i>
                    {category.name}
                  </Link>
                )}
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </Link>
                ))}
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                  {video.region} · {video.year}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                    favorited
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">视频简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {video.description}
              </p>
              <div className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                  {video.uploader.slice(0, 1)}
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{video.uploader}</p>
                  <p className="text-xs text-foreground-500">内容上传者 · 演示数据</p>
                </div>
              </div>
            </div>
          </div>

          <aside>
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-magic-line text-accent-500"></i>
              相关推荐
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex cursor-pointer gap-3 rounded-lg border border-background-200/70 bg-background-50 p-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50"
                >
                  <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 免费在线观看`}
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute bottom-1 right-1 rounded bg-foreground-950/80 px-1 text-[10px] font-label text-background-50">
                      {item.duration}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500">
                      {getCategoryName(item.categoryId)} · {item.type}
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-eye-line"></i>
                      {formatPlayCount(item.playCount)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="animate-fade-in fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </>
  );
}